import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ProfileManagementComponent } from './features/auth/profile-management/profile-management.component';
import { DaysOffConfigComponent } from './features/Availability/days-off-config/days-off-config.component';
import { HoursConfigComponent } from './features/Availability/hours-config/hours-config.component';
import { CancellationComponent } from './features/Cancellation & Refund Policies/cancellation/cancellation.component';
import { EventsComponent } from './features/Events-and-guests/events/events.component';
import { GuestAnnouncementsComponent } from './features/Events-and-guests/guest-announcements/guest-announcements.component';
import { GuestsReviewsComponent } from './features/Events-and-guests/guests-reviews/guests-reviews.component';
import { FloorsManagementComponent } from './features/Floors-and-Kitchens/floors-management/floors-management.component';
import { KitchenManagementComponent } from './features/Floors-and-Kitchens/kitchen-management/kitchen-management.component';
import { ServersAssignmentComponent } from './features/Floors-and-Kitchens/servers-assignment/servers-assignment.component';
import { ErrorComponent } from './features/pages/error/error.component';
import { NotAuthorizedComponent } from './features/pages/not-authorized/not-authorized.component';
import { IngredientsComponent } from './features/restaurants/components/ingredients/ingredients.component';
import { MenuCategoriesComponent } from './features/restaurants/components/menu-categories/menu-categories.component';
import { ProfileComponent } from './features/restaurants/components/profile/profile.component';
import { RestaurantSizesComponent } from './features/restaurants/components/restaurant-sizes/restaurant-sizes.component';
import { AuthGuard } from './helpers/auth.guard';
import { AdminLayoutComponent } from './layouts/admin-layout/admin-layout.component';
import { BasicLayoutComponent } from './layouts/basic-layout/basic-layout.component';
import { EmptyLayoutComponent } from './layouts/empty-layout/empty-layout.component';
import { DirtyCheckGuard } from './helpers/CanDeactivate/dirty-check.guard';
import { PublicPageComponent } from './features/pages/public-page/public-page.component';
import { ClientPayComponent } from './anonymous/client-pay/client-pay.component';
import { TrackOrderComponent } from './anonymous/track-order/track-order.component';
import { AnonymousLayoutComponent } from './layouts/anonymous-layout/anonymous-layout.component';

const routes: Routes = [
  {
    path: '',
    component: BasicLayoutComponent,
    children: [
      { path: '', loadChildren: () => import('./features/auth/auth.module').then(m => m.AuthModule) },
      { path: 'pay', component: ClientPayComponent },
    ]
  },
  {
    path: '',
    component: AnonymousLayoutComponent,
    children: [
      { path: 'trackorder', component: TrackOrderComponent },
    ]
  },
  {
    path: 'admin/floorPlan',
    component: EmptyLayoutComponent,
    children: [
      { path: '', loadChildren: () => import('./features/floor-plan/floor-plan.module').then(m => m.FloorPlanModule) },
    ]
  },
  {
    path: 'admin',
    component: AdminLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', redirectTo: (localStorage.getItem('baseRoute') || ''), pathMatch: 'full' },
      { path: 'profile', component: ProfileManagementComponent },
      { path: 'dashboard', loadChildren: () => import('./features/dashboard/dashboard.module').then(m => m.DashboardModule) },
      { path: 'guestList', loadChildren: () => import('./features/guests/guests.module').then(m => m.GuestsModule) },
      { path: 'staff', loadChildren: () => import('./features/staff/staff.module').then(m => m.StaffModule) },
      { path: 'restaurantProfile', component: ProfileComponent },
      { path: 'restaurants', loadChildren: () => import('./features/restaurants/restaurants.module').then(m => m.RestaurantsModule) },
      { path: 'settings', loadChildren: () => import('./features/settings/settings.module').then(m => m.SettingsModule) },
      { path: 'reports/:view', loadChildren: () => import('./features/reports/reports.module').then(m => m.ReportsModule) },
      { path: 'reportWastedFood', loadChildren: () => import('./features/report-wasted-food/report-wasted-food.module').then(m => m.ReportWastedFoodModule) },
      {
        path: 'configrations',
        children: [
          { path: 'cuisines', loadChildren: () => import('./features/configrations/cuisines/cuisines.module').then(m => m.CuisinesModule) },
          { path: 'seatings', loadChildren: () => import('./features/configrations/seatings/seatings.module').then(m => m.SeatingsModule) },
          { path: 'amenities', loadChildren: () => import('./features/configrations/amenities/amenities.module').then(m => m.AmenitiesModule) },
          { path: 'MeasurementUnits', loadChildren: () => import('./features/configrations/measurement-units/measurement-units.module').then(m => m.MeasurementUnitsModule) },
          { path: 'sizes', loadChildren: () => import('./features/configrations/sizes/sizes.module').then(m => m.SizesModule) },
          { path: 'payment', loadChildren: () => import('./features/configrations/payment/payment.module').then(m => m.PaymentModule) },
        ]
      },
      {
        path: 'availability',
        children: [
          { path: 'daysOffConfig', component: DaysOffConfigComponent },
          { path: 'hoursConfig', component: HoursConfigComponent, canDeactivate: [DirtyCheckGuard] },
        ]
      },
      {
        path: 'stockConfig',
        children: [
          { path: 'MeasurementUnits', loadChildren: () => import('./features/configrations/measurement-units/measurement-units.module').then(m => m.MeasurementUnitsModule) },
          { path: 'IngredientsCategory', loadChildren: () => import('./features/configrations/ingredients-category/ingredients-category.module').then(m => m.IngredientsCategoryModule) },
          { path: 'Ingredients', component: IngredientsComponent, canDeactivate: [DirtyCheckGuard] },
          { path: 'stockTransaction', loadChildren: () => import('./features/Stock Management/stock-transaction/stock-transaction.module').then(m => m.StockTransactionModule) },
          { path: 'suppliers', loadChildren: () => import('./features/Stock Management/suppliers/suppliers.module').then(m => m.SuppliersModule) },
          { path: 'ingredientsStock', loadChildren: () => import('./features/Stock Management/ingredients-stock/ingredients-stock.module').then(m => m.IngredientsStockModule) },
          { path: 'recipeStock', loadChildren: () => import('./features/Stock Management/recipe-stock/recipe-stock.module').then(m => m.RecipeStockModule) },
          { path: 'recipes', loadChildren: () => import('./features/recipes/recipes.module').then(m => m.RecipesModule) },
          { path: 'stockDeductions', loadChildren: () => import('./features/Stock Management/stock-deductions/stock-deductions.module').then(m => m.StockDeductionsModule) },
          { path: 'recipeProduction', loadChildren: () => import('./features/Stock Management/productions/productions.module').then(m => m.ProductionsModule) },
        ]
      },
      {
        path: 'menu-management',
        children: [
          { path: 'menus', loadChildren: () => import('./features/menu-management/menus/menus.module').then(m => m.MenusModule) },
          { path: 'modifiers', loadChildren: () => import('./features/menu-management/modifiers/modifiers.module').then(m => m.ModifiersModule) },
          { path: 'menu-items', loadChildren: () => import('./features/menu-management/menu-items/menu-items.module').then(m => m.MenuItemsModule) },
          { path: 'sizes', component: RestaurantSizesComponent, canDeactivate: [DirtyCheckGuard] },
          { path: 'menu-categories', component: MenuCategoriesComponent },
        ]
      },
      {
        path: 'promotions',
        children: [
          { path: 'promoCode', loadChildren: () => import('./features/Promotions/discounts/discounts.module').then(m => m.DiscountsModule) },
          { path: 'happyHours', loadChildren: () => import('./features/Promotions/happy-hours/happy-hours.module').then(m => m.HappyHoursModule) },
          { path: 'autoDiscount', loadChildren: () => import('./features/Promotions/auto-discounts-config/auto-discounts-config.module').then(m => m.AutoDiscountsConfigModule) },
        ]
      },
      { path: 'inventory', loadChildren: () => import('./features/inventory/inventory.module').then(m => m.InventoryModule) },
      { path: 'floorskitchens/floorPlan', component: FloorsManagementComponent },
      { path: 'floorskitchens/kitchens', component: KitchenManagementComponent },
      { path: 'floorskitchens/serversAssignment', component: ServersAssignmentComponent },
      {
        path: 'eventsAndguests',
        children: [
          { path: 'events', component: EventsComponent },
          { path: 'guestAnnouncements', component: GuestAnnouncementsComponent },
        ]
      },
      { path: 'guestsReviews', component: GuestsReviewsComponent },
      { path: 'dashboard', loadChildren: () => import('./features/dashboard/dashboard.module').then(m => m.DashboardModule) },
      { path: 'cancellation', component: CancellationComponent },
      { path: 'reservations', loadChildren: () => import('./features/reservations/reservations.module').then(m => m.ReservationsModule) },
      { path: 'reservations/:view/place-order', loadChildren: () => import('./features/place-order/place-order.module').then(m => m.PlaceOrderModule) },
      { path: 'orders', loadChildren: () => import('./features/orders/orders.module').then(m => m.OrdersModule) },
      { path: 'orderList', loadChildren: () => import('./features/orders-list/orders-list.module').then(m => m.OrdersListModule) },
      { path: 'reports-track', loadChildren: () => import('./features/reports-track/reports-track.module').then(m => m.ReportsTrackModule) },
      { path: 'reportedReservations', loadChildren: () => import('./features/reported-reservations/reported-reservations.module').then(m => m.ReportedReservationsModule) },
      { path: 'notAuthorized', component: NotAuthorizedComponent },
      { path: 'orderList/takeaway-pickup/placeorder', loadChildren: () => import('./features/place-order/place-order.module').then(m => m.PlaceOrderModule) },
      { path: 'app-reservations', loadChildren: () => import('./features/app-reservations/app-reservations.module').then(m => m.AppReservationsModule) },
      { path: 'app-reservations/placeorder', loadChildren: () => import('./features/place-order/place-order.module').then(m => m.PlaceOrderModule) },
      { path: 'warehouses', loadChildren: () => import('./features/warehouses/warehouses.module').then(m => m.WarehousesModule) },
      { path: 'reward-points', loadChildren: () => import('./features/reward-points/reward-points.module').then(m => m.RewardPointsModule) },
      { path: 'vouchers', loadChildren: () => import('./features/vouchers/vouchers.module').then(m => m.VouchersModule) },
      { path: 'houseAccountPayment', loadChildren: () => import('./features/house-account-payment/house-account-payment.module').then(m => m.HouseAccountPaymentModule) },
      { path: 'DrawerOperations', loadChildren: () => import('./features/drawer-operations/drawer-operations.module').then(m => m.DrawerOperationsModule) },
      { path: 'roles', loadChildren: () => import('./features/roles/roles.module').then(m => m.RolesModule) },
      { path: 'order-returns', loadChildren: () => import('./features/order-return/order-return.module').then(m => m.OrderReturnModule) },
      { path: '**', component: ErrorComponent },
    ]
  },
  { path: 'showMenu', loadChildren: () => import('./anonymous/show-menu/show-menu.module').then(m => m.ShowMenuModule) },
  { path: 'page/:pageName', component: PublicPageComponent },
  { path: '**', component: ErrorComponent }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
